import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ReportsService } from '../../services/reports.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

/**
 * One node of the tree. `path` is the drill path that reaches this node's
 * CHILDREN — i.e. it already includes this node's own step.
 */
interface Node {
	drillkey: string;
	label: string;
	net: number;
	units: number;
	invoices: number;
	customers: number;

	dimension: string;
	dimensionTitle: string;
	depth: number;
	path: string;
	/** Dimension the default expander opens; '' when nothing is left. */
	nextDim: string;
	nextTitle: string;

	/** Children per dimension, so re-opening a node never refetches. */
	childCache: { [dim: string]: Node[] };
	/** Which cached dimension is currently on screen. */
	childDim: string;
	expanded: boolean;
	loading: boolean;
}

/**
 * Brand Sales Ranked by Salesperson — rank a period, then open rows in place:
 * rep → brand → category → product line → item, with "who buys this" available
 * on any node. It expands rather than navigates, so a rep can compare siblings
 * while looking at the detail underneath one of them.
 *
 * This is the rep-safe replacement for the PhpReports report of the same name,
 * and folds in Brand Top Items and BRAND COLOR SALES. The original had an open
 * salesperson picker that applied no filter when left blank, so it could only be
 * granted to Administrator and reps never got it. Here both gates live on the
 * server: a rep only ever sees their own sales, and a rep with rows in
 * salesman_manufacturer only ever sees those brands.
 */
@Component({
	selector: 'app-brand-sales-ranking',
	templateUrl: './brand-sales-ranking.component.html',
	styleUrls: ['./brand-sales-ranking.component.scss']
})
export class BrandSalesRankingComponent implements OnInit {

	@ViewChild('printTable') printTableRef: ElementRef;

	loading = false;
	metaLoading = true;
	exporting = false;

	filterForm: UntypedFormGroup;

	brands: any[] = [];
	productlines: any[] = [];
	categories: any[] = [];
	salespeople: any[] = [];
	dimensions: { value: string; name: string }[] = [];
	startdimensions: { value: string; name: string }[] = [];

	repgated = false;
	brandgated = false;

	roots: Node[] = [];
	totals = { net: 0, units: 0, invoices: 0, customers: 0, count: 0 };
	datefrom = '';
	dateto = '';
	grouptitle = 'Salesperson';

	/** Deepest level currently open — drives the "collapse all" affordance. */
	maxOpenDepth = 0;

	constructor(
		private reportsService: ReportsService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private fb: UntypedFormBuilder
	) {
		const today = new Date();
		const first = new Date(today.getFullYear(), today.getMonth(), 1);
		this.filterForm = this.fb.group({
			from: [this.iso(first)],
			to: [this.iso(today)],
			brands: [[]],
			plines: [[]],
			categories: [[]],
			salespeople: [[]],
			viewby: ['salesperson'],
		});
	}

	ngOnInit(): void {
		// Same move as order entry: a wide ranked table with up to five nested
		// levels needs the horizontal room, so collapse the nav on the way in.
		// The app persists nav state, so we do not force it back open on leave —
		// if the user re-opens the sidebar while here, it stays open.
		this.globalSearchService.hideSideBar();

		this.reportsService.getBrandSalesRankingMeta().subscribe((r: any) => {
			this.metaLoading = false;
			if (!r?.success) { return; }
			this.brands = r.brands || [];
			this.productlines = r.productlines || [];
			this.categories = r.categories || [];
			this.salespeople = r.salespeople || [];
			this.dimensions = r.dimensions || [];
			this.startdimensions = r.startdimensions || this.dimensions;
			this.repgated = !!r.repgated;
			this.brandgated = !!r.brandgated;
			if (r.defaultstart) { this.filterForm.patchValue({ viewby: r.defaultstart }, { emitEvent: false }); }
			this.load();
		}, () => { this.metaLoading = false; });
	}

	private iso(d: Date): string {
		return d.toISOString().substring(0, 10);
	}

	private titleOf(dim: string): string {
		const d = this.dimensions.find(x => x.value === dim);

		return d ? d.name : dim;
	}

	private baseParams() {
		const v = this.filterForm.value;

		return {
			from: v.from,
			to: v.to,
			brands: v.brands || [],
			plines: v.plines || [],
			categories: v.categories || [],
			salespeople: v.salespeople || [],
		};
	}

	/** Root level. Collapses everything — a new filter set invalidates the tree. */
	load(): void {
		this.loading = true;
		this.maxOpenDepth = 0;

		this.reportsService.getBrandSalesRanking({
			...this.baseParams(),
			groupby: this.filterForm.value.viewby || '',
			path: '',
		}).subscribe((r: any) => {
			this.loading = false;
			if (!r?.success) { return; }
			this.totals = r.totals || this.totals;
			this.datefrom = r.datefrom || '';
			this.dateto = r.dateto || '';
			this.grouptitle = r.grouptitle || 'Salesperson';
			this.repgated = !!r.repgated;
			this.brandgated = !!r.brandgated;
			this.roots = (r.rows || []).map((row: any) => this.toNode(row, r.groupby, r.grouptitle, r.nextgroupby, 0, ''));
		}, () => { this.loading = false; });
	}

	private toNode(row: any, dim: string, dimTitle: string, nextDim: string, depth: number, parentPath: string): Node {
		const step = dim + ':' + row.drillkey;

		return {
			drillkey: String(row.drillkey),
			label: String(row.label),
			net: Number(row.net) || 0,
			units: Number(row.units) || 0,
			invoices: Number(row.invoices) || 0,
			customers: Number(row.customers) || 0,
			dimension: dim,
			dimensionTitle: dimTitle,
			depth,
			path: parentPath ? parentPath + ',' + step : step,
			nextDim: nextDim || '',
			nextTitle: nextDim ? this.titleOf(nextDim) : '',
			childCache: {},
			childDim: '',
			expanded: false,
			loading: false,
		};
	}

	rerun(): void {
		this.load();
	}

	reset(): void {
		this.filterForm.patchValue({
			brands: [], plines: [], categories: [], salespeople: [], viewby: 'salesperson',
		});
		this.load();
	}

	canExpand(node: Node): boolean {
		return !!node.nextDim;
	}

	/**
	 * Open a node in place. `dim` overrides the default next dimension — that is
	 * how "who buys this" works: same node, grouped by customer instead.
	 *
	 * Each dimension is fetched at most once per node and kept on the node, so
	 * collapsing and re-opening — or flipping a node between its breakdown and
	 * its customers — costs nothing. Only a filter change (which invalidates
	 * every number) drops the tree and starts over.
	 */
	expand(node: Node, dim?: string): void {
		const want = dim || node.nextDim;
		if (!want) { return; }

		// Clicking the dimension that is already showing means "close it".
		if (node.expanded && node.childDim === want) {
			node.expanded = false;

			return;
		}

		if (node.childCache[want]) {
			node.childDim = want;
			node.expanded = true;
			this.noteDepth(node.depth + 1);

			return;
		}

		node.loading = true;
		this.reportsService.getBrandSalesRanking({
			...this.baseParams(),
			groupby: want,
			path: node.path,
		}).subscribe((r: any) => {
			node.loading = false;
			if (!r?.success) { return; }
			const dimKey = r.groupby || want;
			node.childCache[dimKey] = (r.rows || []).map((row: any) =>
				this.toNode(row, r.groupby, r.grouptitle, r.nextgroupby, node.depth + 1, node.path));
			node.childDim = dimKey;
			node.expanded = true;
			this.noteDepth(node.depth + 1);
		}, () => { node.loading = false; });
	}

	/** Children currently on screen for a node, or [] when closed. */
	childrenOf(node: Node): Node[] {
		return (node.childDim && node.childCache[node.childDim]) || [];
	}

	/** True when this node is open showing `dim` — drives the button states. */
	isShowing(node: Node, dim: string): boolean {
		return node.expanded && node.childDim === dim;
	}

	showCustomers(node: Node): void {
		this.expand(node, 'customer');
	}

	collapse(node: Node): void {
		node.expanded = false;
	}

	collapseAll(): void {
		const walk = (list: Node[]) => list.forEach(n => {
			n.expanded = false;
			Object.keys(n.childCache).forEach(k => walk(n.childCache[k]));
		});
		walk(this.roots);
		this.maxOpenDepth = 0;
	}

	private noteDepth(depth: number): void {
		if (depth > this.maxOpenDepth) { this.maxOpenDepth = depth; }
	}

	/** Depth-first flatten of the open tree — one flat list for one <table>. */
	get visibleRows(): Node[] {
		const out: Node[] = [];
		const walk = (list: Node[]) => list.forEach(n => {
			out.push(n);
			if (n.expanded) { walk(this.childrenOf(n)); }
		});
		walk(this.roots);

		return out;
	}

	get topLabel(): string {
		return this.roots.length ? this.roots[0].label : '—';
	}

	/** Bars are scaled within a node's own sibling set, so every level reads on its own. */
	private siblingsOf(node: Node): Node[] {
		if (node.depth === 0) { return this.roots; }
		const find = (list: Node[]): Node[] | null => {
			for (const n of list) {
				for (const k of Object.keys(n.childCache)) {
					const kids = n.childCache[k];
					if (kids.indexOf(node) > -1) { return kids; }
					const hit = find(kids);
					if (hit) { return hit; }
				}
			}

			return null;
		};

		return find(this.roots) || [node];
	}

	sharePct(node: Node): number {
		const sibs = this.siblingsOf(node);
		const top = sibs.length ? Math.abs(sibs[0].net) : 0;

		return top > 0 ? Math.min((Math.abs(node.net) / top) * 100, 100) : 0;
	}

	shareOfTotal(node: Node): number {
		const sibs = this.siblingsOf(node);
		const total = sibs.reduce((s, n) => s + n.net, 0);

		return total !== 0 ? (node.net / total) * 100 : 0;
	}

	/** Indent caps out so a deep branch never squeezes the label column away. */
	indentPx(node: Node): number {
		return Math.min(node.depth, 4) * 28;
	}

	/** Depth tint class — d1..d4, so two levels never share a background. */
	depthClass(node: Node): string {
		return node.depth > 0 ? 'bsr-d' + Math.min(node.depth, 4) : '';
	}

	/**
	 * True when this row opens a new sibling group, i.e. it is the first child of
	 * its parent. Used to draw the boundary that says "this block belongs to the
	 * row above".
	 */
	isGroupStart(node: Node): boolean {
		if (node.depth === 0) { return false; }

		return this.siblingsOf(node).indexOf(node) === 0;
	}

	rankOf(node: Node): number {
		return this.siblingsOf(node).indexOf(node) + 1;
	}

	// ── Export ───────────────────────────────────────────────────────────────
	// Exports what is on screen, open branches included — "give me what I am
	// looking at" rather than a separate flat dump that would not match it.

	get hasExportableRows(): boolean {
		return this.roots.length > 0;
	}

	private get exportTitle(): string {
		return 'Brand Sales by ' + this.grouptitle;
	}

	private get exportSubtitle(): string {
		const v = this.filterForm.value;
		const scope = [];
		if (this.repgated) { scope.push('your sales only'); }
		if (this.brandgated) { scope.push('your brands only'); }

		return v.from + ' to ' + v.to
			+ (scope.length ? ' · ' + scope.join(', ') : '')
			+ ' · created ' + new Date().toLocaleString();
	}

	private get exportFilename(): string {
		return 'brand-sales-by-' + this.grouptitle.toLowerCase().replace(/\s+/g, '-')
			+ '-' + this.filterForm.value.from;
	}

	/**
	 * Flatten the tree into something a converter can render.
	 *
	 * Order matters. Icons go first: the exporters read text nodes, so a ligature
	 * would otherwise land in a cell as the literal word "expand_more" or
	 * "groups". Then the share bars go — they are pure decoration and would
	 * export as empty cells wide enough to wreck the column widths. Buttons are
	 * removed outright rather than unwrapped (unlike promo-intro, where headers
	 * are buttons): here every button is a drill control, so its label would land
	 * in a data cell as a stray "Category".
	 *
	 * The indent is inline margin the exporters drop, so nesting is re-expressed
	 * as text — "-- BRAND: KEVIN MURPHY" — or the export would read as one flat
	 * list with no way to tell a rep's row from a category three levels down.
	 */
	private buildExportHtml(): string {
		const clone = this.printTableRef.nativeElement.cloneNode(true) as HTMLElement;

		clone.querySelectorAll('mat-icon, .mat-icon, i.material-icons').forEach((n: Element) => n.remove());
		clone.querySelectorAll('.bsr-bar, mat-progress-spinner').forEach((n: Element) => n.remove());
		clone.querySelectorAll('button').forEach((n: Element) => n.remove());

		// The actions column holds only drill controls, so once the buttons are gone
		// it is an empty trailing column in every row — and an empty column in the
		// spreadsheet. Drop it from the header and every body row.
		clone.querySelectorAll('tr').forEach((row: Element) => {
			const last = row.lastElementChild;
			if (last && (last.textContent || '').trim() === '' && !last.getAttribute('data-label')) {
				last.remove();
			}
		});

		clone.querySelectorAll('tbody tr').forEach((row: Element) => {
			const depth = parseInt(row.getAttribute('data-depth') || '0', 10);
			const cell = row.children[1] as HTMLElement;
			if (!cell) { return; }
			const dim = cell.querySelector('.bsr-dim');
			const dimText = dim ? (dim.textContent || '').trim() : '';
			if (dim) { dim.remove(); }
			const name = (cell.textContent || '').replace(/\s+/g, ' ').trim();
			const prefix = depth > 0 ? '-'.repeat(depth * 2) + ' ' : '';
			cell.textContent = prefix + (dimText ? dimText + ': ' : '') + name;
		});

		return clone.innerHTML;
	}

	exportXls(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		if (!this.printTableRef) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportHtml()),
			filename: this.exportFilename,
			title: this.exportTitle,
			subtitle: this.exportSubtitle,
			// Seven columns plus long customer names do not fit portrait.
			landscape: true,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}
}
